import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import ImageUpload from '@/components/ImageUpload';

export default function AdminGalleryImages() {
  const [images, setImages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  const [formData, setFormData] = useState({
    image_url: '',
    title: '',
    description: '',
    order: 0
  });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await apiClient.get('/gallery-images');
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        id: editingImage?.id || undefined
      };

      if (editingImage) {
        await apiClient.put(`/admin/gallery-images/${editingImage.id}`, payload);
        toast.success('Gallery image updated successfully!');
      } else {
        await apiClient.post('/admin/gallery-images', payload);
        toast.success('Gallery image created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchImages();
    } catch (error) {
      toast.error('Failed to save gallery image');
    }
  };

  const handleEdit = (image) => {
    setEditingImage(image);
    setFormData({
      image_url: image.image_url,
      title: image.title,
      description: image.description,
      order: image.order
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await apiClient.delete(`/admin/gallery-images/${id}`);
        toast.success('Gallery image deleted successfully!');
        fetchImages();
      } catch (error) {
        toast.error('Failed to delete gallery image');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      image_url: '',
      title: '',
      description: '',
      order: 0
    });
    setEditingImage(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Manage Gallery</h1>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Image
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {images.map((image) => (
            <div key={image.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <img src={image.image_url} alt={image.title} className="w-full h-48 object-cover" />
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">{image.title || 'Untitled'}</h3>
                <p className="text-gray-600 text-sm mb-2 line-clamp-2">{image.description}</p>
                <span className="text-xs text-gray-500">Order: {image.order}</span>
                <div className="flex space-x-2 mt-3">
                  <Button onClick={() => handleEdit(image)} size="sm" variant="outline">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => handleDelete(image.id)} size="sm" variant="destructive">
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingImage ? 'Edit Gallery Image' : 'Add New Gallery Image'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <ImageUpload
              label="Gallery Image"
              value={formData.image_url}
              onChange={(url) => setFormData({ ...formData, image_url: url })}
              required={true}
              preview={true}
            />
            <div>
              <Label>Title (Optional)</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Image title"
              />
            </div>
            <div>
              <Label>Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description"
                rows={2}
              />
            </div>
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                placeholder="0"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Image
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
